import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons/core-free-icons";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";
import { compactGroupClass, pillGroupClass, pillItemClass, secondaryPillClass } from "./pill-classes";

export function SecondaryPill({ className, ...props }: ComponentProps<"button">) {
  return <button type="button" className={cn(secondaryPillClass, className)} {...props} />;
}

export type PillOption<K extends string = string> = { value: K; label: string; count?: number };

export function PillSwitch<K extends string>({
  label,
  options,
  value,
  onChange,
  compact,
}: {
  label: string;
  compact?: boolean;
  options: PillOption<K>[];
  value: K;
  onChange: (value: K) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={compact ? compactGroupClass : pillGroupClass}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={pillItemClass(o.value === value, compact)}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count === undefined ? null : <span className="text-xs tabular-nums opacity-60">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Static read-only stat pill, hidden below md. */
export function StatPill({ label, value, title }: { label: string; value: string | number; title?: string }) {
  return (
    <span className={`${secondaryPillClass} max-md:hidden`} title={title}>
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

export function FooterSearch({
  value,
  onChange,
  placeholder = "Search",
  label,
  className,
}: {
  value: string;
  onChange: (q: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "flex h-11 w-44 items-center gap-2 rounded-full bg-card px-4 text-muted-foreground focus-within:ring-2 focus-within:ring-ring max-md:min-w-0 max-md:flex-1",
        className,
      )}
    >
      <HugeiconsIcon icon={Search01Icon} strokeWidth={1.8} className="size-4 shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label ?? placeholder}
        className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
      />
    </label>
  );
}
